/**
 * subscriber-discount.js
 * Auto-applied first-purchase discount + contextual celebration popup.
 * JS is a messenger only — Shopify enforces eligibility at checkout.
 */
(function () {
  'use strict';

  // Bail on browsers without fetch (very old IE/Opera Mini); the cart-banner
  // and Liquid-rendered UI still work without us, so failing silently is fine.
  if (typeof fetch !== 'function') return;

  var SESSION_KEY  = 'discount:applied';
  var POPUP_KEY    = 'first_purchase_popup_shown_';   // localStorage prefix
  var POPUP_COOKIE = 'fp_popup_shown_';               // cookie fallback prefix
  var YEAR_SECONDS = 31536000;

  // Suppress popup on cart/checkout, including locale-prefixed paths
  // like /en-in/cart, /fr/checkouts/abc, etc.
  var SKIP_POPUP  = /^(?:\/[a-z]{2,5}(?:-[a-z]{2,5})?)?\/(cart|checkout|checkouts)(\/|$)/i;

  // Home / front page (with optional locale prefix). Popup is anchored here
  // — anywhere else after login (including /account/* paths like the orders
  // page Shopify sometimes lands customers on), we redirect to home so the
  // popup renders there.
  var HOME_RE     = /^(?:\/[a-z]{2,5}(?:-[a-z]{2,5})?)?\/?$/i;

  var applying  = false; // in-flight guard so DOMContentLoaded + race calls don't double-fetch
  var modalOpen = false; // prevent duplicate modal if showPopup is called twice

  function cfg() {
    var c = window.SHOPIFY_CONFIG;
    return (c && typeof c === 'object') ? c : {};
  }
  function hasCookie(n) { return new RegExp('(^|; )' + n + '=').test(document.cookie); }
  function ls(k, v) { try { return v === undefined ? localStorage.getItem(k)   : localStorage.setItem(k, v); }   catch (e) {} }
  function ss(k, v) { try { return v === undefined ? sessionStorage.getItem(k) : sessionStorage.setItem(k, v); } catch (e) {} }
  function safeFocus(el) { try { if (el && typeof el.focus === 'function') el.focus(); } catch (e) {} }
  function raf(fn) { return (typeof requestAnimationFrame === 'function' ? requestAnimationFrame : function (cb) { return setTimeout(cb, 16); })(fn); }

  function autoApplyDiscount() {
    if (applying) return;
    var c = cfg();
    if (!c.isLoggedIn || c.ordersCount !== 0 || !c.discountCode) return;
    if (hasCookie('discount_code')) return;
    if (ss(SESSION_KEY) === c.discountCode) return;

    applying = true;
    fetch('/discount/' + encodeURIComponent(c.discountCode) + '?redirect=/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        ss(SESSION_KEY, c.discountCode);
      })
      .catch(function (e) { console.warn('[discount] apply failed:', e); })
      .then(function () { applying = false; }); // finally-style cleanup (ES5-safe)
  }

  // Welcome / acknowledgment popup — anchored to the home page.
  //   /        → show the popup right here
  //   anywhere → redirect to / so the popup shows there on the next load
  // The redirect is naturally one-shot because POPUP_KEY (set by markShown
  // the moment the popup renders on home) blocks re-entry on every later
  // page load. Cart/checkout are pre-excluded by SKIP_POPUP.
  function maybeShowWelcomePopup() {
    var c = cfg();

    // Force-check localStorage first thing — if this customer has ever
    // seen the popup on this device, bail immediately. Nothing below
    // this line can re-trigger the popup once the flag is set.
    if (c.customerId && ls(POPUP_KEY + c.customerId)) return;

    if (!c.isLoggedIn || c.ordersCount !== 0) return;
    if (!shouldShowPopup()) return;

    var path = location.pathname;
    if (HOME_RE.test(path)) {
      // Already on the home page — render the popup right here.
      showPopup();
      return;
    }

    // Anywhere else (including /account/orders and other /account/* pages) —
    // bounce to home so the popup renders there.
    // replace() avoids piling up the previous URL in history.
    location.replace('/');
  }

  function isPopupShown(customerId) {
    if (!customerId) return false;
    // Either store wins — defense-in-depth against blocked localStorage
    if (ls(POPUP_KEY + customerId)) return true;
    if (hasCookie(POPUP_COOKIE + customerId)) return true;
    return false;
  }

  function shouldShowPopup() {
    if (SKIP_POPUP.test(location.pathname)) return false;
    var c = cfg();
    return !!c.customerId && !isPopupShown(c.customerId);
  }

  function markShown() {
    var c = cfg();
    if (!c.customerId) return;
    ls(POPUP_KEY + c.customerId, '1');
    // Cookie fallback for browsers that block localStorage
    // (Brave shields, Safari ITP, private modes, third-party-storage policies).
    document.cookie = POPUP_COOKIE + c.customerId + '=1;max-age=' + YEAR_SECONDS + ';path=/;SameSite=Lax';
  }

  function showPopup() {
    if (modalOpen) return;
    var c = cfg();

    // Final force-check before painting anything: re-read localStorage
    // (in case another tab dismissed the popup between shouldShowPopup()
    // and now) and bail if the flag is set.
    if (c.customerId && ls(POPUP_KEY + c.customerId)) return;

    // Mark shown the moment we render — not on dismissal.
    // Guarantees the popup never appears twice for this customer
    // even if they close the tab, kill the page, or never click any button.
    markShown();

    var prev = document.activeElement;
    var body = (c.popupBody || '').replace('%value%', c.discountValue || '');

    var bg = document.createElement('div');
    bg.className = 'discount-modal-backdrop';
    bg.innerHTML =
      '<div class="discount-modal" role="dialog" aria-modal="true" aria-labelledby="discount-modal-title">' +
        '<button type="button" class="discount-modal__close" aria-label="Close">×</button>' +
        '<h2 id="discount-modal-title" class="discount-modal__title"></h2>' +
        '<p class="discount-modal__body"></p>' +
        '<button type="button" class="discount-modal__cta"></button>' +
      '</div>';

    var titleEl = bg.querySelector('.discount-modal__title');
    var bodyEl  = bg.querySelector('.discount-modal__body');
    var closeBt = bg.querySelector('.discount-modal__close');
    var cta     = bg.querySelector('.discount-modal__cta');
    if (!titleEl || !bodyEl || !closeBt || !cta) return; // defensive against innerHTML rejection

    titleEl.textContent = c.popupHeadline || 'Welcome';
    bodyEl.textContent  = body;
    cta.textContent     = c.popupCtaLabel || 'Shop now';

    document.body.appendChild(bg);
    modalOpen = true;
    raf(function () { bg.classList.add('is-open'); });

    var btns  = bg.querySelectorAll('button');
    var first = btns[0];
    var last  = btns[btns.length - 1];

    function close(href) {
      if (!modalOpen) return; // idempotent
      // markShown() already ran at render time; no need to re-mark here.
      modalOpen = false;
      bg.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      setTimeout(function () { if (bg.parentNode) bg.parentNode.removeChild(bg); }, 200);
      safeFocus(prev);
      if (href) window.location.href = href;
    }
    function onKey(e) {
      if (e.key === 'Escape') return close();
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { safeFocus(last);  e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { safeFocus(first); e.preventDefault(); }
    }

    bg.addEventListener('click', function (e) { if (e.target === bg) close(); });
    closeBt.addEventListener('click', function () { close(); });
    cta.addEventListener('click', function () { close(c.popupCtaUrl || null); });
    document.addEventListener('keydown', onKey);
    safeFocus(cta);
  }

  // If the customer arrived via the newsletter popup CTA (which sets a
  // sessionStorage flag right before navigating to login), clear any stored
  // POPUP_KEY / cookie so the welcome popup is guaranteed to fire on this
  // post-login page load regardless of prior dismissals.
  function consumeForceShow() {
    try {
      if (sessionStorage.getItem('fp_force_welcome') !== '1') return;
      sessionStorage.removeItem('fp_force_welcome');
      var c = cfg();
      if (!c.customerId) return;
      try { localStorage.removeItem(POPUP_KEY + c.customerId); } catch (e) {}
      document.cookie = POPUP_COOKIE + c.customerId +
        '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function init() {
    consumeForceShow();        // unblocks the popup if user came via CTA
    autoApplyDiscount();       // fire-and-forget; idempotent guards inside
    maybeShowWelcomePopup();   // independent of the fetch
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
